<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <b-row align-v="center" class="mb-2">
        <b-col cols="auto"><h1 class="mb-0">Menú de plataformas</h1></b-col>
        <b-col cols="auto">
          <b-form-select v-model="menuId" :options="menuOptions" size="sm" @change="loadMenu"
            style="min-width: 220px;"></b-form-select>
        </b-col>
        <b-col cols="auto" v-if="menuId">
          <b-button size="sm" variant="outline-secondary" @click="loadMenu" :disabled="loading">
            <b-spinner v-if="loading" small></b-spinner> Recargar
          </b-button>
        </b-col>
      </b-row>

      <b-alert v-if="!menuId && !loading" show variant="info">
        Selecciona un menú para empezar. Los menús se crean desde la pantalla de plataformas.
      </b-alert>

      <b-row v-if="menuId">
        <!-- ── Panel izquierdo: biblioteca de grupos ─────────────────────── -->
        <b-col md="3">
          <b-card no-body class="mb-3">
            <b-card-header class="py-2 d-flex justify-content-between align-items-center">
              <strong>Grupos de modificadores</strong>
              <b-button size="sm" variant="primary" @click="newGroup">+</b-button>
            </b-card-header>
            <b-list-group flush>
              <b-list-group-item v-for="g in modifierGroups" :key="g.id" button
                @click="editGroup(g)" class="py-2">
                <div class="d-flex justify-content-between align-items-center">
                  <span :class="{ 'text-muted': !g.is_active }">{{ g.name }}</span>
                  <small class="text-muted">{{ optionsOf(g.id).length }} opc.</small>
                </div>
                <small class="text-muted">
                  {{ g.required_min }}–{{ g.required_max }}
                  <span v-if="g.required_min > 0" class="text-danger">· obligatorio</span>
                </small>
              </b-list-group-item>
              <b-list-group-item v-if="!modifierGroups.length" class="text-muted py-3 text-center">
                <small>Sin grupos todavía.</small>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>

        <!-- ── Panel central: categorías e items ─────────────────────────── -->
        <b-col md="5">
          <b-card no-body>
            <b-card-header class="py-2 d-flex justify-content-between align-items-center">
              <strong>Categorías</strong>
              <span>
                <b-button size="sm" variant="outline-primary" @click="newCategory">+ Categoría</b-button>
                <b-button size="sm" variant="primary" class="ml-1" @click="newProduct"
                  :disabled="!categories.length">+ Producto</b-button>
              </span>
            </b-card-header>
            <div v-for="cat in categories" :key="cat.id">
              <div class="px-3 py-2 bg-light d-flex justify-content-between align-items-center border-bottom">
                <strong :class="{ 'text-muted': !cat.is_active }">{{ cat.name }}</strong>
                <small class="text-muted">{{ itemsOf(cat.id).length }}</small>
              </div>
              <b-list-group flush>
                <b-list-group-item v-for="it in itemsOf(cat.id)" :key="cat.id + it.id" button
                  :active="selected && selected.id === it.id" @click="select(it)" class="py-2">
                  <div class="d-flex justify-content-between">
                    <span>
                      {{ it.name }}
                      <b-badge v-if="!it.is_active" variant="secondary" class="ml-1">inactivo</b-badge>
                      <b-badge v-if="!it.external_id" variant="danger" class="ml-1"
                        title="Sin producto del POS: no se puede convertir en ticket">sin POS</b-badge>
                    </span>
                    <span class="text-muted">
                      <small v-if="groupsOf(it.id).length" class="mr-2">
                        {{ groupsOf(it.id).length }} grupo(s)
                      </small>
                      ${{ Number(it.price).toFixed(2) }}
                    </span>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
            <div v-if="!categories.length" class="p-4 text-center text-muted">
              <small>Crea una categoría para empezar a colgar productos.</small>
            </div>
          </b-card>
        </b-col>

        <!-- ── Panel derecho: detalle del item ───────────────────────────── -->
        <b-col md="4">
          <b-card no-body v-if="selected">
            <b-card-header class="py-2"><strong>{{ selected.name }}</strong></b-card-header>
            <b-card-body>
              <b-alert v-if="!form.external_id" show variant="danger" class="py-2">
                <small>Sin producto del POS ligado. El pedido no se podrá convertir en ticket.</small>
              </b-alert>

              <b-form-group label="Producto del POS" description="Define el external_id con el que se materializa la línea.">
                <b-form-select v-model="formRef" :options="posOptions" size="sm"></b-form-select>
              </b-form-group>
              <b-form-group label="Nombre">
                <b-form-input v-model="form.name" size="sm"></b-form-input>
              </b-form-group>
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Precio">
                    <b-form-input v-model.number="form.price" type="number" step="0.01" size="sm"></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Activo">
                    <b-form-checkbox v-model="form.is_active" switch class="mt-1">
                      {{ form.is_active ? 'sí' : 'no' }}
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Descripción">
                <b-form-textarea v-model="form.description" rows="2" size="sm"></b-form-textarea>
              </b-form-group>
              <b-button size="sm" variant="success" @click="saveItem" :disabled="saving">Guardar</b-button>
              <b-button size="sm" variant="outline-danger" class="float-right" @click="removeItem">Eliminar</b-button>

              <hr>
              <strong class="d-block mb-2">Grupos de modificadores</strong>
              <p class="text-muted"><small>Marca los grupos que este producto ofrece.</small></p>
              <b-form-checkbox v-for="g in modifierGroups" :key="'chk' + g.id"
                :checked="hasGroup(selected.id, g.id)" @change="toggleGroup(g, $event)" class="mb-1">
                {{ g.name }}
                <small class="text-muted">({{ g.required_min }}–{{ g.required_max }}, {{ optionsOf(g.id).length }} opc.)</small>
              </b-form-checkbox>
              <div v-if="!modifierGroups.length" class="text-muted"><small>No hay grupos creados.</small></div>
            </b-card-body>
          </b-card>
          <b-card v-else class="text-center text-muted">
            <small>Selecciona un producto para editarlo.</small>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <!-- ── Modal: alta rápida de categoría / producto ────────────────────── -->
    <b-modal v-model="showNew" :title="newKind === 'category' ? 'Nueva categoría' : 'Nuevo producto'"
      ok-title="Crear" :ok-disabled="!newName.trim()" @ok="confirmNew">
      <b-form-group label="Nombre">
        <b-form-input v-model="newName" autofocus @keyup.enter="confirmNew(null)"></b-form-input>
      </b-form-group>
      <b-form-group v-if="newKind === 'product'" label="Categoría">
        <b-form-select v-model="newCategoryId" :options="categoryOptions" size="sm"></b-form-select>
      </b-form-group>
      <small v-if="newKind === 'product'" class="text-muted">
        Después podrás ligarlo a un producto del POS y asignarle grupos de modificadores.
      </small>
    </b-modal>

    <!-- ── Modal: grupo de modificadores y sus opciones ──────────────────── -->
    <b-modal v-model="showGroup" :title="groupForm.id ? 'Grupo: ' + groupForm.name : 'Nuevo grupo'"
      size="lg" ok-title="Guardar" @ok="saveGroup">
      <b-row>
        <b-col md="6">
          <b-form-group label="Nombre"><b-form-input v-model="groupForm.name" size="sm"></b-form-input></b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Mín."><b-form-input v-model.number="groupForm.required_min" type="number" size="sm"></b-form-input></b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Máx."><b-form-input v-model.number="groupForm.required_max" type="number" size="sm"></b-form-input></b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Activo">
            <b-form-checkbox v-model="groupForm.is_active" switch class="mt-1"></b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
      <small class="text-muted">Mín. 0 = opcional. Máx. = cuántas opciones puede elegir el cliente.</small>

      <template v-if="groupForm.id">
        <hr>
        <strong>Opciones</strong>
        <b-table small :items="optionRows" :fields="optionFields" class="mt-2" show-empty
          empty-text="Sin opciones. Agrega productos del POS abajo.">
          <template #cell(price)="data">${{ Number(data.item.price).toFixed(2) }}</template>
          <template #cell(acciones)="data">
            <b-button size="sm" variant="outline-danger" @click="removeOption(data.item)">Quitar</b-button>
          </template>
        </b-table>

        <b-row align-v="end">
          <b-col md="6">
            <b-form-group label="Agregar producto del POS como opción" class="mb-1">
              <b-form-select v-model="newOption.ref" :options="posOptions" size="sm"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Precio extra" class="mb-1">
              <b-form-input v-model.number="newOption.price" type="number" step="0.01" size="sm"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-button size="sm" variant="primary" block @click="addOption"
              :disabled="!newOption.ref || addingOption">Agregar</b-button>
          </b-col>
        </b-row>
        <small class="text-muted">
          Cargo extra que verá el cliente. Si lo dejas en 0 se usa el precio del
          producto del POS; para que salga sin costo, ese producto debe valer $0.
        </small>
      </template>
      <b-alert v-else show variant="info" class="mt-3 mb-0 py-2">
        <small>Guarda el grupo para poder agregarle opciones.</small>
      </b-alert>

      <template #modal-footer="{ ok, cancel }">
        <b-button v-if="groupForm.id" size="sm" variant="outline-danger" class="mr-auto"
          @click="deleteGroup">Eliminar grupo</b-button>
        <b-button size="sm" @click="cancel()">Cerrar</b-button>
        <b-button size="sm" variant="primary" @click="ok()">Guardar</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import svc from "../../services/platformMenuService";

export default {
  name: "platformMenuEditor",
  components: { Nav },
  data() {
    return {
      loading: false,
      saving: false,
      addingOption: false,
      menus: [],
      menuId: null,
      unified: null,
      posProducts: [],
      posPersonalizations: [],
      selected: null,
      form: {},
      showGroup: false,
      groupForm: {},
      newOption: { ref: null, price: 0 },
      showNew: false,
      newKind: "category",
      newName: "",
      newCategoryId: null,
      optionFields: [
        { key: "name", label: "Opción" },
        { key: "price", label: "Precio" },
        { key: "acciones", label: "" },
      ],
    };
  },
  computed: {
    menuOptions() {
      return [{ value: null, text: "— selecciona un menú —" }].concat(
        this.menus.map((m) => ({ value: m.id, text: m.name }))
      );
    },
    // Las dos cosas a las que puede apuntar external_id, en un solo selector.
    // El valor lleva el tipo dentro porque el id solo es ambiguo: el 67 puede
    // ser el producto 67 o la personalización 67.
    posOptions() {
      return [{ value: null, text: "— sin ligar —" }].concat(
        this.posProducts.map((p) => ({
          value: "product::" + p.id,
          text: "Producto · " + (p.clave ? p.clave + " · " : "") + (p.short_name || p.ndescription || "#" + p.id),
        })),
        this.posPersonalizations.map((p) => ({
          value: "personalization::" + p.id,
          text: "Personalización · " + (p.name || p.short_name || "#" + p.id),
        }))
      );
    },
    // Empaqueta/desempaqueta external_id + ref_type para el selector del item.
    formRef: {
      get() {
        if (!this.form.external_id) return null;
        return (this.form.ref_type === "personalization" ? "personalization::" : "product::") + this.form.external_id;
      },
      set(val) {
        if (!val) { this.form.external_id = null; this.form.ref_type = "product"; return; }
        const [tipo, id] = String(val).split("::");
        this.form.ref_type = tipo === "personalization" ? "personalization" : "product";
        this.form.external_id = Number(id);
      }
    },
    categories() {
      const cats = ((this.unified && this.unified.categories) || []).slice();
      cats.sort((a, b) => (a.sorting_position || 0) - (b.sorting_position || 0));
      return cats;
    },
    modifierGroups() {
      return (this.unified && this.unified.modifier_groups) || [];
    },
    // Solo los items de tipo producto se listan; los MODIFIER viven dentro de los grupos.
    itemById() {
      const map = {};
      ((this.unified && this.unified.items) || []).forEach((i) => { map[i.id] = i; });
      return map;
    },
    categoryOptions() {
      return this.categories.map((c) => ({ value: c.id, text: c.name }));
    },
    optionRows() {
      if (!this.groupForm.id) return [];
      return this.optionsOf(this.groupForm.id).map((o) => {
        const it = this.itemById[o.item_id] || {};
        return { item_id: o.item_id, name: it.name || o.item_id, price: o.price };
      });
    },
  },
  mounted() {
    this.loadMenus();
    svc.posProducts().then((r) => { this.posProducts = r.data || []; }).catch(() => {});
    svc.posPersonalizations().then((r) => { this.posPersonalizations = r.data || []; }).catch(() => {});
  },
  methods: {
    isModifier(it) {
      return String(it.type || "").toUpperCase() === "MODIFIER";
    },
    itemsOf(categoryId) {
      const links = ((this.unified && this.unified.category_items) || [])
        .filter((ci) => ci.category_id === categoryId)
        .slice()
        .sort((a, b) => (a.sorting_position || 0) - (b.sorting_position || 0));
      return links.map((l) => this.itemById[l.item_id]).filter((i) => i && !this.isModifier(i));
    },
    optionsOf(groupId) {
      return ((this.unified && this.unified.modifier_items) || []).filter((o) => o.modifier_group_id === groupId);
    },
    groupsOf(itemId) {
      return ((this.unified && this.unified.item_modifier_groups) || []).filter((l) => l.item_id === itemId);
    },
    hasGroup(itemId, groupId) {
      return this.groupsOf(itemId).some((l) => l.modifier_group_id === groupId);
    },
    loadMenus() {
      svc.menus()
        .then((r) => {
          this.menus = r.data || [];
          if (this.menus.length === 1) { this.menuId = this.menus[0].id; this.loadMenu(); }
        })
        .catch((e) => this.err(e));
    },
    loadMenu() {
      if (!this.menuId) return;
      this.loading = true;
      svc.unified(this.menuId)
        .then((r) => {
          this.unified = r.data || null;
          if (this.selected) {
            const fresh = this.itemById[this.selected.id];
            if (fresh) this.select(fresh); else this.selected = null;
          }
        })
        .catch((e) => this.err(e))
        .finally(() => (this.loading = false));
    },
    select(it) {
      this.selected = it;
      this.form = {
        name: it.name,
        description: it.description || "",
        price: Number(it.price) || 0,
        external_id: it.external_id || null,
        ref_type: it.ref_type === "personalization" ? "personalization" : "product",
        is_active: !!it.is_active,
      };
    },
    saveItem() {
      this.saving = true;
      const payload = {
        ...this.selected, ...this.form,
        external_id: this.form.external_id || 0,
        ref_type: this.form.ref_type || "product",
      };
      svc.updateItem(this.selected.id, payload)
        .then(() => { this.ok("Producto guardado"); this.loadMenu(); })
        .catch((e) => this.err(e))
        .finally(() => (this.saving = false));
    },
    removeItem() {
      this.confirm(`¿Eliminar "${this.selected.name}" del menú?`).then((yes) => {
        if (!yes) return;
        svc.deleteItem(this.selected.id)
          .then(() => { this.selected = null; this.ok("Producto eliminado"); this.loadMenu(); })
          .catch((e) => this.err(e));
      });
    },
    newCategory() {
      this.newKind = "category";
      this.newName = "";
      this.showNew = true;
    },
    newProduct() {
      this.newKind = "product";
      this.newName = "";
      this.newCategoryId = this.categories.length ? this.categories[0].id : null;
      this.showNew = true;
    },
    confirmNew(evt) {
      if (evt) evt.preventDefault();
      const name = this.newName.trim();
      if (!name) return;
      if (this.newKind === "category") {
        svc.createCategory(this.menuId, {
          name, is_active: true, sorting_position: this.categories.length,
        })
          .then(() => { this.showNew = false; this.ok("Categoría creada"); this.loadMenu(); })
          .catch((e) => this.err(e));
        return;
      }
      const catId = this.newCategoryId;
      if (!catId) { this.err({ message: "Selecciona una categoría" }); return; }
      svc.createItem(this.menuId, { name, type: "PRODUCT", price: 0, is_active: true, external_id: 0 })
        .then((r) => svc.addItemToCategory(catId, r.data.id))
        .then(() => { this.showNew = false; this.ok("Producto creado"); this.loadMenu(); })
        .catch((e) => this.err(e));
    },
    // ── Grupos ──────────────────────────────────────────────────────────
    newGroup() {
      this.groupForm = { name: "", required_min: 0, required_max: 1, is_active: true };
      this.newOption = { ref: null, price: 0 };
      this.showGroup = true;
    },
    editGroup(g) {
      this.groupForm = { ...g };
      this.newOption = { external_id: null, price: 0 };
      this.showGroup = true;
    },
    saveGroup(evt) {
      evt.preventDefault();
      const req = this.groupForm.id
        ? svc.updateModifierGroup(this.groupForm.id, this.groupForm)
        : svc.createModifierGroup(this.menuId, this.groupForm);
      req
        .then((r) => {
          // Al crear, deja el modal abierto con el id nuevo para cargarle opciones.
          if (!this.groupForm.id && r.data && r.data.id) this.groupForm = { ...r.data };
          else this.showGroup = false;
          this.ok("Grupo guardado");
          this.loadMenu();
        })
        .catch((e) => this.err(e));
    },
    deleteGroup() {
      this.confirm(`¿Eliminar el grupo "${this.groupForm.name}"?`).then((yes) => {
        if (!yes) return;
        svc.deleteModifierGroup(this.groupForm.id)
          .then(() => { this.showGroup = false; this.ok("Grupo eliminado"); this.loadMenu(); })
          .catch((e) => this.err(e));
      });
    },
    // Crea (si hace falta) el platform_item de tipo MODIFIER y lo mete al grupo.
    addOption() {
      if (!this.newOption.ref) return;
      const [tipo, rawId] = String(this.newOption.ref).split("::");
      const refType = tipo === "personalization" ? "personalization" : "product";
      const refId = Number(rawId);
      const src = refType === "personalization"
        ? this.posPersonalizations.find((p) => p.id === refId)
        : this.posProducts.find((p) => p.id === refId);
      if (!src) return;
      this.addingOption = true;
      const name = src.short_name || src.name || src.ndescription || ("#" + src.id);
      // El item existente se busca por id Y por tipo: sin el tipo, la
      // personalización 67 se confundiría con el producto 67.
      const existing = ((this.unified && this.unified.items) || [])
        .find((i) => this.isModifier(i) && i.external_id === refId
                     && (i.ref_type || "product") === refType);
      const ensureItem = existing
        ? Promise.resolve({ data: existing })
        : svc.createItem(this.menuId, {
            name, type: "MODIFIER", price: Number(this.newOption.price) || 0,
            is_active: true, external_id: refId, ref_type: refType,
          });
      ensureItem
        .then((r) => svc.addOptionToGroup(this.groupForm.id, r.data.id, {
          price: Number(this.newOption.price) || 0,
          min_qty: 0, max_qty: 1, type: "MODIFIER",
          sorting_position: this.optionsOf(this.groupForm.id).length,
        }))
        .then(() => { this.newOption = { external_id: null, price: 0 }; this.ok("Opción agregada"); this.loadMenu(); })
        .catch((e) => this.err(e))
        .finally(() => (this.addingOption = false));
    },
    removeOption(row) {
      svc.removeOptionFromGroup(this.groupForm.id, row.item_id)
        .then(() => { this.ok("Opción quitada"); this.loadMenu(); })
        .catch((e) => this.err(e));
    },
    toggleGroup(g, checked) {
      const req = checked
        ? svc.attachGroupToItem(this.selected.id, g.id)
        : svc.detachGroupFromItem(this.selected.id, g.id);
      req.then(() => this.loadMenu()).catch((e) => { this.err(e); this.loadMenu(); });
    },
    // ── utilidades ──────────────────────────────────────────────────────
    confirm(msg) {
      return this.$bvModal.msgBoxConfirm(msg, {
        title: "Confirmar", okTitle: "Sí", cancelTitle: "No", okVariant: "danger",
      });
    },
    ok(msg) {
      this.$bvToast.toast(msg, { title: "Listo", variant: "success", autoHideDelay: 4000, appendToast: true });
    },
    err(e) {
      const d = e && e.response && e.response.data;
      const msg = typeof d === "string" ? d : (d && d.error) || (e && e.message) || "Error desconocido";
      this.$bvToast.toast(msg, { title: "Error", variant: "danger", autoHideDelay: 6000, appendToast: true });
    },
  },
};
</script>
